/**
 * Designer (installers' menu): open an existing design in the editor, or start a new one from an address.
 *
 * - Open: the company's designs, most recently changed first, searchable by address, name or email, each with
 *   its version and a thumbnail of its imagery (loaded when it scrolls into view).
 * - New: the calculator's address search (`/v1/orgs/{org}/places/…`, Google Places with the server key), then
 *   "Confirm the property": the calculator's pin map (`/v1/orgs/{org}/site-map`, signed satellite images) where the
 *   pin is dragged onto the right building (that point is the design's location), the roof type (`roof-types`, the
 *   company's default pre-selected) and the Lux it uses. The design then builds like a website quote; a progress
 *   panel follows the job (`/v1/jobs/{id}`: friendly step names, bar, time, errors with a retry) and opens the
 *   editor when it is ready.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLuxSpend } from "../components/LuxSpend";
import { activityHref, ratePrice } from "../lib/luxSpend";
import { Check, Loader2, LocateFixed, MapPin, PenTool, Plus, RotateCcw, Search, Sparkles, X, ZoomIn, ZoomOut } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useLuxWallet } from "../api/queries";
import { fmtLux } from "../lib/lux";
import { Alert, Badge, Button, Card, Empty, Field, Modal, PageHead, Select, Spinner } from "../components/ui";
import { LuxGlyph } from "../components/LuxIcon";
import { config } from "../config";
import { clock, etaDeadline, failure, headline, percent, remaining, steps } from "../lib/designProgress";
import { date, shownVersion } from "../lib/format";
import { ADDRESS_MAX_LENGTH, addressQuery, distanceM, type LatLng, offerZoom, parseCoordinates, pinPosition, pointAt } from "../lib/siteMap";
import { leadName, leadState, useMinuteTick, ViewChip, VIEWS_REFRESH_MS } from "./Designs";
import { sitePictureUrl, useDarkTheme } from "../lib/theme";
import { useIntlShape, useT } from "../i18n/I18nProvider";

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

function newSession(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

/** The design's imagery as a small picture, fetched only once it is on screen. A Sketch has no imagery: its roof
 * drawn from the geometry, with its panels (`?panels=1`). */
function Thumb({ lead }: { lead: S["Lead"] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const dark = useDarkTheme();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => entry?.isIntersecting && setVisible(true), { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!visible || !lead.current_version_number || !lead.kw_dc) return;
    let url: string | null = null;
    let live = true;
    rawFetch(sitePictureUrl(`/api/v1/designs/${lead.design_id}/versions/${lead.current_version_number}/site/image`, { sketch: lead.design_mode === "sketch", dark, panels: true }))
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => {
        if (!live) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [visible, lead.design_id, lead.current_version_number, lead.kw_dc, lead.design_mode, dark]);
  return (
    <div className={`designer-thumb${lead.design_mode === "sketch" ? " is-sketch" : ""}`} ref={ref}>
      {src ? <img src={src} alt="" /> : lead.design_mode === "sketch" ? <PenTool size={18} /> : <MapPin size={18} />}
      {lead.design_mode === "sketch" && src && <span className="designer-thumb-mark" aria-hidden><PenTool size={10} /></span>}
    </div>
  );
}

function OpenDesign() {
  const t = useT();
  const orgId = useOrgId();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const query = useDebounced(q.trim(), 300);
  useMinuteTick();
  const leads = useInfiniteQuery({
    queryKey: ["designer-leads", orgId, query],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/orgs/{org_id}/leads", { params: { path: { org_id: orgId }, query: { limit: 24, cursor: pageParam, q: query || undefined } } })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
    refetchInterval: VIEWS_REFRESH_MS, // new quote views appear without a reload (paused while the tab is hidden)
  });
  const items = leads.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card title={t("designer.open.title")} description={t("designer.open.description")}>
      <div className="stack">
        <div className="input-icon">
          <Search />
          <input className="input" placeholder={t("designer.open.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("designer.open.searchLabel")} />
        </div>
        {leads.isLoading ? (
          <Spinner label={t("designer.loading")} />
        ) : leads.error ? (
          <Alert tone="danger">{errorMessage(leads.error)}</Alert>
        ) : !items.length ? (
          <Empty icon={<Search />} title={query ? t("designer.open.noMatches") : t("designer.open.empty")}>
            {query ? t("designer.open.noMatchesBody") : t("designer.open.emptyBody")}
          </Empty>
        ) : (
          <ul className="designer-list">
            {items.map((lead) => {
              const state = leadState(lead);
              const sketch = lead.design_mode === "sketch";
              const ready = sketch || (!!lead.current_version_number && !!lead.kw_dc);
              const name = leadName(lead);
              return (
                <li key={lead.design_id}>
                  <button className="designer-item" onClick={() => navigate(sketch ? `/designs/${lead.design_id}/sketch` : ready ? `/designs/${lead.design_id}/edit` : `/designs/${lead.design_id}`)} title={sketch ? t("designer.open.openSketch") : ready ? t("designer.open.openEditor") : t("designer.open.notBuilt")}>
                    <Thumb lead={lead} />
                    <span className="designer-meta">
                      <b className="ellipsis">{lead.address || lead.design_name}</b>
                      <span className="muted small ellipsis">
                        {lead.current_version_number ? t("designer.open.version", { version: shownVersion(lead.current_version_number, lead.version_offset) }) : t("designer.open.noVersion")} · {date(lead.updated_at)}
                        {name ? ` · ${name}` : ""}
                      </span>
                      {lead.views && (
                        <span className="designer-views">
                          <ViewChip views={lead.views} />
                        </span>
                      )}
                    </span>
                    {sketch && <Badge tone="info" plain><PenTool size={11} /> Sketch</Badge>}
                    <Badge tone={state.tone}>{state.label}</Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {leads.hasNextPage && (
          <Button onClick={() => leads.fetchNextPage()} loading={leads.isFetchingNextPage}>
            {t("designer.open.loadMore")}
          </Button>
        )}
      </div>
    </Card>
  );
}

function NewDesign() {
  const t = useT();
  const orgId = useOrgId();
  const wallet = useLuxWallet(orgId);
  const balance = wallet.data?.balance ?? null;
  const [text, setText] = useState("");
  const [session, setSession] = useState(newSession);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState<{ address: string; lat: number; lng: number; place_id: string; jurisdiction?: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [suggestions, setSuggestions] = useState<S["PlaceSuggestion"][]>([]);
  const [searching, setSearching] = useState(false);
  // Past the company's address searches (plan limit): no suggestions; the address is typed and the roof found by
  // its coordinates on the pin map instead.
  const [limited, setLimited] = useState(false);
  const [coords, setCoords] = useState("");
  const input = useDebounced(addressQuery(text) ?? "", 300);

  useEffect(() => {
    if (!input || picked?.address === text || limited) {
      setSearching(false);
      return setSuggestions([]);
    }
    let live = true;
    setSearching(true);
    unwrap(api.GET("/v1/orgs/{org_id}/places/autocomplete", { params: { path: { org_id: orgId }, query: { input, session } } }))
      .then((r) => live && (setSuggestions(r.suggestions), setActive(0), setError(null), setLimited(!!r.limited)))
      .catch((e) => live && setError(errorMessage(e)))
      .finally(() => live && setSearching(false));
    return () => {
      live = false;
    };
  }, [input, session, orgId, picked, text, limited]);
  const typed = parseCoordinates(coords);

  const coordsRow = (
    <div className="row">
      <input className="input" style={{ maxWidth: 280 }} placeholder={t("designer.new.coordsPlaceholder", { example: "30.2614, -97.7270" })} value={coords} aria-label={t("designer.new.coordsLabel")} onChange={(e) => setCoords(e.target.value)} />
      <Button
        disabled={!typed || !text.trim()}
        onClick={() => typed && setPicked({ address: text.trim(), lat: typed.lat, lng: typed.lng, place_id: `manual:${typed.lat},${typed.lng}` })}
      >
        {t("designer.new.useCoords")}
      </Button>
    </div>
  );

  const choose = async (s: S["PlaceSuggestion"]) => {
    setOpen(false);
    setText(s.description);
    try {
      const place = await unwrap(api.GET("/v1/orgs/{org_id}/places/{place_id}", { params: { path: { org_id: orgId, place_id: s.place_id }, query: { session } } }));
      if (!place.success || !place.location) return setError(place.error || t("designer.new.addressNotFound"));
      setPicked({ address: place.formatted_address || s.description, lat: place.location.lat, lng: place.location.lng, place_id: s.place_id, jurisdiction: place.jurisdiction ?? null });
      setSession(newSession()); // a Places session ends with the details request
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Card title={t("designer.new.title")} description={t("designer.new.description", { cost: fmtLux(100) })}>
      <div className="stack">
        <div className="designer-search">
          <div className="input-icon">
            <MapPin />
            <input
              className="input"
              placeholder={t("designer.new.addressPlaceholder")}
              value={text}
              maxLength={ADDRESS_MAX_LENGTH}
              aria-label={t("designer.new.addressLabel")}
              aria-autocomplete="list"
              aria-expanded={open && suggestions.length > 0}
              onChange={(e) => {
                setText(e.target.value);
                setPicked(null);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => window.setTimeout(() => setOpen(false), 150)}
              onKeyDown={(e) => {
                if (!open || !suggestions.length) return;
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((i) => (i + (e.key === "ArrowDown" ? 1 : suggestions.length - 1)) % suggestions.length);
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  choose(suggestions[active] ?? suggestions[0]!);
                } else if (e.key === "Escape") setOpen(false);
              }}
            />
          </div>
          {open && suggestions.length > 0 && (
            <ul className="designer-suggestions" role="listbox">
              {suggestions.map((s, i) => (
                <li key={s.place_id} role="option" aria-selected={i === active} className={i === active ? "on" : ""} onMouseDown={(e) => { e.preventDefault(); choose(s); }}>
                  <MapPin size={14} />
                  <span>
                    <b>{s.main_text}</b> <span className="muted small">{s.secondary_text}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {searching && <span className="muted small">{t("designer.new.searching")}</span>}
        {limited && (
          <Alert tone="warn" title={t("designer.new.limitedTitle")}>
            <div className="stack" style={{ gap: 8 }}>
              <span>{t("designer.new.limitedBody")}</span>
              {coordsRow}
            </div>
          </Alert>
        )}
        {error && !limited && (
          // address search is down: the same way through, by coordinates (a dead end before: Create stayed disabled)
          <Alert tone="warn" title={error}>
            <div className="stack" style={{ gap: 8 }}>
              <span>{t("designer.new.coordsHint")}</span>
              {coordsRow}
            </div>
          </Alert>
        )}
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="muted small row" style={{ gap: 6 }}>
            <LuxGlyph size={14} /> {balance == null ? "…" : t("designer.new.luxLeft", { amount: fmtLux(balance) })}
          </span>
          <Button variant="primary" icon={<Plus size={16} />} disabled={!picked} onClick={() => setConfirming(true)}>
            {t("designer.new.create")}
          </Button>
        </div>
      </div>
      {picked && confirming && <NewDesignFlow key={`${picked.place_id}:${picked.lat}:${picked.lng}`} picked={picked} balance={balance} onClose={() => setConfirming(false)} />}
    </Card>
  );
}

type Picked = { address: string; lat: number; lng: number; place_id: string; /** The house's country and region from the place (CA-AB): the design keeps it. */ jurisdiction?: string | null };

/** The steps after the address: confirm the building on the map and the roof type (then 1 Lux is spent), and
 * the build's progress until the editor opens. */
export function NewDesignFlow({ picked, balance, onClose }: { picked: Picked; balance: number | null; onClose: () => void }) {
  const t = useT();
  const intl = useIntlShape();
  const orgId = useOrgId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pin, setPin] = useState<LatLng>({ lat: picked.lat, lng: picked.lng });
  const [roofType, setRoofType] = useState<string | null>(null);
  // Sketch (free: drawn by the installer) or automatic (1.00 Lux: the pipeline); Sketch when no Lux is left
  const [mode, setMode] = useState<"sketch" | "automatic">(balance != null && balance <= 0 ? "sketch" : "automatic");
  const idempotency = useMemo(() => newSession(), []); // one key per confirmation: a double click is one design
  const lux = useLuxSpend();
  const wallet = useLuxWallet(orgId);
  const designCost = ratePrice(wallet.data?.rate_card, "design", 100);
  const roofTypes = useQuery({
    queryKey: ["roof-types", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/roof-types", { params: { path: { org_id: orgId } } })),
    staleTime: 5 * 60_000,
  });
  const chosenRoof = roofType ?? roofTypes.data?.default_roof_type ?? null;
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/designs", {
          params: { path: { org_id: orgId }, header: { "Idempotency-Key": idempotency } },
          body: { address: picked.address, lat: round6(pin.lat), lng: round6(pin.lng), place_id: picked.place_id.startsWith("manual:") ? null : picked.place_id, jurisdiction: picked.jurisdiction ?? null, roof_type: chosenRoof, mode },
        }),
      ),
    onSuccess: (made) => {
      if (made.mode === "sketch") navigate(`/designs/${made.design_id}/sketch`);
      else lux.spent({ amount: designCost, label: t("designer.confirm.designWithAi"), left: made.lux_left ?? null, href: activityHref("design") });
      qc.invalidateQueries({ queryKey: ["lux", orgId] });
      qc.invalidateQueries({ queryKey: ["designer-leads", orgId] });
      qc.invalidateQueries({ queryKey: ["leads-infinite", orgId] });
    },
  });

  if (create.data && create.data.mode !== "sketch")
    return (
      <BuildProgress
        designId={create.data.design_id}
        jobId={create.data.job_id ?? null}
        address={picked.address}
        jobOptions={create.data.job_options ?? {}}
        onClose={onClose}
        onReady={() => navigate(`/designs/${create.data!.design_id}/edit`)}
      />
    );
  const none = balance != null && balance <= 0;
  const moved = distanceM({ lat: picked.lat, lng: picked.lng }, pin);
  return (
    <Modal
      title={t("designer.confirm.title")}
      onClose={onClose}
      width={720}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("designer.confirm.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={async (e) => {
              const anchor = e.currentTarget;
              if (mode === "automatic" && !(await lux.confirm({ cost: designCost, label: t("designer.confirm.designWithAi"), balance, worthMinor: wallet.data?.unit_minor != null ? Math.floor((designCost * wallet.data.unit_minor) / 100) : null }, anchor))) return;
              create.mutate();
            }}
            loading={create.isPending}
            disabled={(mode === "automatic" && none) || roofTypes.isLoading}
          >
            {mode === "sketch" ? t("designer.confirm.startSketch") : t("designer.confirm.createAi")}
          </Button>
        </>
      }
    >
      <div className="stack designer-confirm">
        <div className="designer-confirm-address">
          <MapPin size={18} />
          <span className="designer-meta">
            <b>{picked.address}</b>
            <span className="muted small">
              {moved < 1 ? t("designer.confirm.pinOnAddress") : t("designer.confirm.pinMoved", { meters: Math.round(moved) })}
            </span>
          </span>
        </div>
        <PinMap orgId={orgId} start={{ lat: picked.lat, lng: picked.lng }} pin={pin} onPin={setPin} />
        <Field label={t("designer.confirm.roofType")} hint={t("designer.confirm.roofTypeHint")}>
          {roofTypes.error ? (
            <Alert tone="danger">{errorMessage(roofTypes.error)}</Alert>
          ) : (
            <Select
              ariaLabel={t("designer.confirm.roofType")}
              value={chosenRoof}
              disabled={!roofTypes.data}
              onChange={(v) => setRoofType(v)}
              options={(roofTypes.data?.roof_types ?? []).map((rt) => ({ value: rt.id, label: rt.id === roofTypes.data?.default_roof_type ? t("designer.confirm.companyDefault", { label: rt.label }) : rt.label }))}
            />
          )}
        </Field>
        <div className="designer-modes" role="radiogroup" aria-label={t("designer.confirm.howToDesign")}>
          <button type="button" role="radio" aria-checked={mode === "sketch"} className={`designer-mode is-sketch${mode === "sketch" ? " on" : ""}`} onClick={() => setMode("sketch")}>
            <span className="designer-mode-icon" aria-hidden><PenTool size={18} /></span>
            <span className="designer-mode-body">
              <span className="designer-mode-head"><b>{t("designer.confirm.sketchTitle")}</b><span className="designer-mode-price">{t("designer.confirm.free")}</span></span>
              <span className="muted small">{t("designer.confirm.sketchBody")}</span>
            </span>
            <span className="designer-mode-check" aria-hidden><Check size={12} strokeWidth={3} /></span>
          </button>
          <button type="button" role="radio" aria-checked={mode === "automatic"} className={`designer-mode is-ai${mode === "automatic" ? " on" : ""}`} onClick={() => setMode("automatic")}>
            <span className="designer-mode-icon" aria-hidden><Sparkles size={18} /></span>
            <span className="designer-mode-body">
              <span className="designer-mode-head"><b>{t("designer.confirm.designWithAi")}</b><span className="designer-mode-price">{fmtLux(100)} Lux</span></span>
              <span className="muted small">{t("designer.confirm.aiBody")}</span>
            </span>
            <span className="designer-mode-check" aria-hidden><Check size={12} strokeWidth={3} /></span>
          </button>
        </div>
        {mode === "automatic" && (<Alert tone={none ? "danger" : "info"} title={t("designer.confirm.usesLux", { cost: fmtLux(100) })}>
          {none
            ? intl.formatMessage({ id: "designer.confirm.noLux" }, { link: (c) => <Link to="/lux">{c}</Link> })
            : balance != null
              ? t("designer.confirm.balance", { balance: fmtLux(balance), after: fmtLux(balance - 100) })
              : t("designer.confirm.editingFree")}
        </Alert>)}
        {create.error && <Alert tone="danger">{errorMessage(create.error)}</Alert>}
      </div>
    </Modal>
  );
}

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

/** The calculator's signed satellite image with a pin the user drags onto the building (the widget's pin map). */
function PinMap({ orgId, start, pin, onPin }: { orgId: string; start: LatLng; pin: LatLng; onPin: (p: LatLng) => void }) {
  const t = useT();
  const viewRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(20);
  const [centre, setCentre] = useState<LatLng>(start);
  const [size, setSize] = useState(0);
  const [loaded, setLoaded] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const map = useQuery({
    queryKey: ["site-map", orgId, centre.lat.toFixed(6), centre.lng.toFixed(6), zoom],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/site-map", { params: { path: { org_id: orgId }, query: { lat: centre.lat, lng: centre.lng, zoom } } })),
    staleTime: 10 * 60_000,
    retry: 1,
  });
  useLayoutEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    const read = () => setSize(el.clientWidth);
    const ro = new ResizeObserver(read);
    ro.observe(el);
    read();
    return () => ro.disconnect();
  }, []);
  // Zooming is offered only for a large building (the imagery stage's 400 m² ground area), or when its size is unknown.
  const building = useQuery({
    queryKey: ["site-building", orgId, start.lat.toFixed(6), start.lng.toFixed(6)],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/site-building", { params: { path: { org_id: orgId }, query: { lat: start.lat, lng: start.lng } } })),
    staleTime: 10 * 60_000,
    retry: 1,
  });
  const zoomable = offerZoom(building.data, !!building.error);
  const m = map.data;
  const src = m ? `${config.apiUrl}${m.image_url}` : null;
  const ready = !!m && loaded === src;
  const at = (e: ReactPointerEvent) => {
    const el = viewRef.current;
    if (!el || !m) return;
    const r = el.getBoundingClientRect();
    onPin(pointAt(m, e.clientX - r.left, e.clientY - r.top, r.width, r.height));
  };
  const pos = m && size ? pinPosition(m, pin, size, size) : null;
  const zoomBy = (d: number) => {
    if (!m) return;
    const next = Math.max(m.min_zoom, Math.min(m.max_zoom, zoom + d));
    if (next === zoom) return;
    setCentre(pin); // re-centred on the pin
    setZoom(next);
  };
  const off = distanceM(start, pin) >= 1;
  return (
    <div className="pin-map-wrap">
      <div
        ref={viewRef}
        className={`pin-map${dragging ? " is-dragging" : ""}`}
        onPointerDown={(e) => {
          if ((e.target as HTMLElement).closest(".pin-map-tools") || !ready) return;
          setDragging(true);
          e.currentTarget.setPointerCapture?.(e.pointerId);
          at(e);
        }}
        onPointerMove={(e) => dragging && at(e)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        role="application"
        aria-label={t("designer.map.label")}
      >
        {src && !failed && <img src={src} alt={t("designer.map.imageAlt")} draggable={false} onLoad={() => setLoaded(src)} onError={() => setFailed(true)} style={{ opacity: ready ? 1 : 0 }} />}
        {!ready && !failed && !map.error && (
          <div className="pin-map-note">
            <Spinner label={t("designer.loading")} />
          </div>
        )}
        {(failed || map.error) && <div className="pin-map-note muted small">{t("designer.map.failed")}</div>}
        {/* the map never mirrors: the pin keeps its pixel position (physical left) */}
        {ready && pos && (
          <span className="pin-map-pin" style={{ left: Math.max(0, Math.min(size, pos.x)), top: Math.max(0, Math.min(size, pos.y)) }} aria-hidden>
            <MapPin size={34} strokeWidth={2.2} />
          </span>
        )}
        {m && <span className="pin-map-credit">{m.attribution}</span>}
        <div className="pin-map-tools">
          {zoomable && (
            <>
              <button type="button" className="icon-btn" onClick={() => zoomBy(1)} disabled={!m || zoom >= m.max_zoom} aria-label={t("designer.map.zoomIn")}>
                <ZoomIn size={16} />
              </button>
              <button type="button" className="icon-btn" onClick={() => zoomBy(-1)} disabled={!m || zoom <= m.min_zoom} aria-label={t("designer.map.zoomOut")}>
                <ZoomOut size={16} />
              </button>
            </>
          )}
          <button type="button" className="icon-btn" onClick={() => { onPin(start); setCentre(start); }} disabled={!off} aria-label={t("designer.map.resetPin")} title={t("designer.map.resetPinTitle")}>
            <LocateFixed size={16} />
          </button>
        </div>
      </div>
      <p className="muted small" style={{ margin: 0 }}>{zoomable ? t("designer.map.hintZoom") : t("designer.map.hint")}</p>
    </div>
  );
}

/** The first build's progress: the job's steps with friendly names, a bar, the time so far, errors with a retry; the
 * editor opens when it is done. */
export function BuildProgress({ designId, jobId, address, jobOptions, onClose, onReady }: {
  designId: string;
  jobId: string | null;
  address: string;
  /** The first build's stage options (the roof material): a retry passes them again. */
  jobOptions: Record<string, Record<string, unknown>>;
  onClose: () => void;
  onReady: () => void;
}) {
  const t = useT();
  const [currentJob, setCurrentJob] = useState<string | null>(jobId);
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const job = useQuery({
    queryKey: ["job", currentJob],
    queryFn: () => unwrap(api.GET("/v1/jobs/{job_id}", { params: { path: { job_id: currentJob! } } })),
    enabled: !!currentJob,
    refetchInterval: (q) => (q.state.data && ["succeeded", "failed", "canceled"].includes(q.state.data.status) ? false : 1500),
  });
  const data = job.data;
  const finished = data?.status === "succeeded";
  const failedJob = data && (data.status === "failed" || data.status === "canceled") ? data : null;
  useEffect(() => {
    if (finished || failedJob) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [finished, failedJob]);
  useEffect(() => {
    if (!finished) return;
    const t = window.setTimeout(onReady, 900); // let the full bar show
    return () => window.clearTimeout(t);
  }, [finished, onReady]);
  const retry = useMutation({
    mutationFn: () =>
      unwrap(api.POST("/v1/designs/{design_id}/jobs", { params: { path: { design_id: designId }, header: { "Idempotency-Key": newSession() } }, body: { options: jobOptions, force: false } })),
    onSuccess: (j) => {
      setCurrentJob(j.id);
      setStartedAt(Date.now());
      setNow(Date.now());
    },
  });
  // the estimate only counts down (see etaDeadline); a retry is a new job with its own
  const deadline = useRef<{ job: string | null; at: number | null }>({ job: null, at: null });
  if (deadline.current.job !== currentJob) deadline.current = { job: currentJob, at: null };
  deadline.current.at = etaDeadline(deadline.current.at, data, Date.now());
  const left = remaining(data, deadline.current.at, now, t);
  const pct = percent(data);
  const why = failedJob ? failure(failedJob, t) : null;
  return (
    <Modal
      title={failedJob ? t("designer.build.failedTitle") : finished ? t("designer.build.readyTitle") : t("designer.build.buildingTitle")}
      onClose={onClose}
      width={560}
      footer={
        failedJob ? (
          <>
            <Link className="btn btn-ghost" to={`/designs/${designId}`}>
              {t("designer.build.openPage")}
            </Link>
            <Button variant="primary" icon={<RotateCcw size={16} />} onClick={() => retry.mutate()} loading={retry.isPending}>
              {t("designer.build.retry")}
            </Button>
          </>
        ) : finished ? (
          <Button variant="primary" onClick={onReady}>
            {t("designer.build.openEditor")}
          </Button>
        ) : (
          <Button variant="ghost" onClick={onClose} title={t("designer.build.backgroundTitle")}>
            {t("designer.build.background")}
          </Button>
        )
      }
    >
      <div className="stack build-progress" aria-live="polite">
        <div className="designer-confirm-address">
          <MapPin size={18} />
          <b>{address}</b>
        </div>
        <div className="build-progress-head">
          <span>{headline(data, t)}</span>
          <span className="muted small">
            {clock((finished || failedJob ? Date.now() : now) - startedAt)}
            {left ? ` · ${left}` : ""}
          </span>
        </div>
        <div className="progress build-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={t("designer.build.progressLabel")}>
          <span style={{ width: `${Math.max(pct, 3)}%` }} className={failedJob ? "failed" : ""} />
        </div>
        <ol className="build-steps">
          {steps(data, t).map((s) => (
            <li key={s.stage} className={s.state}>
              <span className="build-step-icon" aria-hidden>
                {s.state === "done" ? <Check size={14} /> : s.state === "active" ? <Loader2 size={14} className="spin" /> : s.state === "failed" ? <X size={14} /> : null}
              </span>
              <span>{s.label}</span>
            </li>
          ))}
        </ol>
        {!currentJob && <Alert tone="warn">{t("designer.build.notStarted")}</Alert>}
        {job.error && <Alert tone="warn">{t("designer.build.checkFailed", { error: errorMessage(job.error) })}</Alert>}
        {why && (
          <Alert tone="danger">
            {why.message} {t("designer.build.retryFree")}
          </Alert>
        )}
        {retry.error && <Alert tone="danger">{errorMessage(retry.error)}</Alert>}
      </div>
    </Modal>
  );
}

export function Designer() {
  const t = useT();
  const { can } = useOrg();
  return (
    <>
      <PageHead
        title={t("designer.page.title")}
        description={t("designer.page.description")}
        actions={
          <Link className="btn" to="/designs">
            <PenTool size={16} /> {t("designer.page.allDesigns")}
          </Link>
        }
      />
      <div className="designer-grid">
        {can("designs_leads", "create") && <NewDesign />}
        <OpenDesign />
      </div>
    </>
  );
}
